import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  WidgetsState,
  UserWidgetsModel,
  UserWidgetsState,
  CaseBaseModel,
} from 'models';

import services from 'services';

const modifyUserWidget = (userWidget: UserWidgetsModel): UserWidgetsState => ({
  CaseWidget: userWidget.CaseWidget?.Cases || null,
  ReservationWidget: userWidget.ReservationWidget || null,
  WelcomeWidget: userWidget.WelcomeWidget,
  NewButtonWidget: userWidget.NewButtonWidget
    ? {
        CanCreateCase: userWidget.NewButtonWidget?.CanCreateCase || false,
        CanCreateKeyOrder:
          userWidget.NewButtonWidget?.CanCreateKeyOrder || false,
        CanCreateRapair: userWidget.NewButtonWidget?.CanCreateRapair || false,
        CanCreateReservation:
          userWidget.NewButtonWidget?.CanCreateReservation || false,
        CaseLevels1: userWidget.NewButtonWidget?.CaseLevels1 || [],
        CaseLevels2: userWidget.NewButtonWidget?.CaseLevels2 || [],
      }
    : null,
  ContactInformationWidget: {
    Accountant: userWidget.ContactInformationWidget?.Accountant || null,
    PropertyMaintenance:
      userWidget.ContactInformationWidget?.PropertyMaintenance || null,
    PropertyManager:
      userWidget.ContactInformationWidget?.PropertyManager || null,
  },
  MessageWidget: userWidget.MessageWidget?.Messages || null,
  FinanceWidget: userWidget.FinanceWidget,
  CooperativeBalanceSituationWidget:
    userWidget.CooperativeBalanceSituationWidget,
  CooperativeBudgetWidget: userWidget.CooperativeBudgetWidget,
  TaskWidget: userWidget.TaskWidget?.Tasks || null,
});

export const fetchWidgets = createAsyncThunk(
  'widgets/fetchWidgets',
  async (url: string) => {
    const res = (await services.get(url)).data.Data;

    return res as UserWidgetsModel;
  }
);

export const fetchMessageWidget = createAsyncThunk(
  'widgets/fetchMessageWidget',
  async (coopId: string) => {
    const res: UserWidgetsModel['MessageWidget'] = (
      await services.get(`/api/user/cooperatives/${coopId}/widget/message`)
    ).data.Data;

    return res;
  }
);

const initialState: WidgetsState = {
  showWelcomeWidget: true,
  userWidgets: {
    CaseWidget: null,
    ReservationWidget: null,
    WelcomeWidget: null,
    NewButtonWidget: {
      CanCreateCase: false,
      CanCreateKeyOrder: false,
      CanCreateRapair: false,
      CanCreateReservation: false,
      CaseLevels1: [],
      CaseLevels2: [],
    },
    ContactInformationWidget: {
      Accountant: null,
      PropertyMaintenance: null,
      PropertyManager: null,
    },
    MessageWidget: null,
    FinanceWidget: null,
    CooperativeBalanceSituationWidget: null,
    CooperativeBudgetWidget: null,
    TaskWidget: null,
  },
  loading: false,
  error: false,
};

const widgetsSlice = createSlice({
  name: 'widgets',
  initialState,
  reducers: {
    setShowWelcomeWidget(state, action: PayloadAction<boolean>) {
      state.showWelcomeWidget = action.payload;
    },
    modifyBaseCase(state, action: PayloadAction<CaseBaseModel>) {
      return {
        ...state,
        userWidgets: {
          ...state.userWidgets,
          CaseWidget: state.userWidgets.CaseWidget
            ? state.userWidgets.CaseWidget.map((baseCase) => {
                return baseCase.Id === action.payload.Id
                  ? { ...action.payload }
                  : baseCase;
              })
            : null,
        },
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchWidgets.pending, (state) => {
      state.error = false;
      state.loading = true;
    });
    builder.addCase(fetchWidgets.fulfilled, (state, action) => {
      const userWidgets = action.payload;

      if (userWidgets) {
        state.userWidgets = modifyUserWidget(userWidgets);
        state.loading = false;
      }
    });
    builder.addCase(fetchWidgets.rejected, (state) => {
      state.loading = false;
      state.error = true;
    });
    builder.addCase(fetchMessageWidget.fulfilled, (state, action) => {
      return {
        ...state,
        userWidgets: {
          ...state.userWidgets,
          MessageWidget: action.payload?.Messages || null,
        },
      };
    });
  },
});

export const { reducer } = widgetsSlice;
export const { setShowWelcomeWidget, modifyBaseCase } = widgetsSlice.actions;
